/* eslint-disable no-unused-vars */
import React from 'react';
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  Menu,
  MenuItem,
  useTheme,
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

import {
  usePopupState,
  bindTrigger,
  bindMenu,
} from 'material-ui-popup-state/hooks';

import clsx from 'clsx';
import LockOutlined from '@mui/icons-material/LockOutlined';

interface OwnProps {
  readonly options: any[];
  readonly filters: any[];
  readonly overrideFilterState?: boolean;
  readonly singleSelect?: boolean;
  readonly defaultLabel: string;
  readonly locked?: boolean;
  readonly backgroundOverride?: string;
  readonly defaultLabelOnOne?: boolean;
  readonly AddFilter?: (filter: any) => void;
  readonly RemoveFilter?: (filter: any) => void;
  readonly SetSingleFilter?: (filter:any) => void;
  readonly labelPropertyName:string;
  readonly GetDefaultLabel?: (filters: any[], defaultLabel: string) => string;
  readonly GetOptionLabel?: (option: any) => string;
}

type Props = OwnProps;

const Component = (props: Props) => {
  const popupState = usePopupState({
    variant: 'popover',
    popupId: 'filter-multi-select',
  });
  const {
    filters,
    singleSelect,
    defaultLabel,
    locked,
    options,
    defaultLabelOnOne,
    AddFilter,
    RemoveFilter,
    SetSingleFilter,
    labelPropertyName,
    GetDefaultLabel,
    GetOptionLabel
  } = props;

  const getLabel = GetDefaultLabel ? GetDefaultLabel : (filters: any[], defaultLabel: string): string => {
    if (filters?.length === 1 && !defaultLabelOnOne && filters[0] !== undefined && filters[0][labelPropertyName]) {
      const  filter= filters[0];
      return filter[labelPropertyName];
    }
    if (singleSelect &&  filters[0] !== undefined && filters[0][labelPropertyName]) {
      return filters[0][labelPropertyName];
    }

    if (defaultLabel) {
      return defaultLabel;
    }
    return defaultLabel + 's';
  };

  const getOptionLabel = GetOptionLabel ? GetOptionLabel : (option: any): string => { return option[labelPropertyName] }

  const theme = useTheme();

  const handleChange = (event: any, option: string) => {
    if (singleSelect && SetSingleFilter) {
      SetSingleFilter(option);
     popupState.setOpen(false);
    } else {
      const toAdd = event.target.checked;
      if (toAdd && AddFilter) {
        AddFilter(option);
      } else if (!toAdd && RemoveFilter) {
        RemoveFilter(option);
      }
    }
  };

  const EndIcon = () => {
    if (locked) {
      return <LockOutlined fontSize='small' color="primary" />
    }
    if (filters?.length > 0 && !singleSelect) {
      return <Box sx={{
        borderRadius: '50%',
        width: 24,
        height: 24,
        background: theme.palette.primary.main,
        color: theme.palette.common.white,
        textAlign: 'center',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        fontSize: '12px !important',
        lineHeight: 1,
      }}>
        {filters?.length}
      </Box>
    }
    return <ArrowDropDownIcon color="secondary" />
  }

  const CustomMenuItem = (option:any) => {
    if (singleSelect) {
      return (
        <MenuItem dense key={getOptionLabel(option)} onClick={(e) => handleChange(e, option)} sx={{
          width: "100%",
          '& .MuiTypography-root': {
            fontWeight: 500,
          },
          '& .MuiListItemText-dense': {
            marginTop: 2,
            marginBottom: 2,
          },
        }}>
          {getOptionLabel(option)}
        </MenuItem>
      )
    }
    return (
      <MenuItem dense key={option} sx={{
        '& .MuiTypography-root': {
          fontWeight: 500,
        },
        '& .MuiListItemText-dense': {
          marginTop: 2,
          marginBottom: 2,
        },
      }}>
        <FormControl key={getOptionLabel(option)} id={getOptionLabel(option)} sx={{
          width: '100%',
          justifyContent: 'space-between',
          textTransform: 'capitalize',
          '& .MuiFormControlLabel-root': {
            marginLeft: 0,
            marginRight: 0,
            justifyContent: 'space-between',
          },
          '& .MuiFormControlLabel-label': {
            fontSize: '0.875rem',
            fontWeight: 500,
          },
          '& .MuiCheckbox-root': {
            padding: 0,
          },
        }}>
          <FormGroup>
            <FormControlLabel
              key={getOptionLabel(option)}
              value="start"
              control={
                <Checkbox
                  checked={!!filters?.find((fd) => fd === getOptionLabel(option))}
                  size="small"
                  color="secondary"
                  onChange={(e) => handleChange(e, option)}
                />
              }
              label={getOptionLabel(option)}
              labelPlacement="start"
              sx={{
                justifyContent: 'space-between !important',
              }}
            />
          </FormGroup>
        </FormControl>
      </MenuItem>
    )
  }

  return (
    <Box data-filter="custom-filter" sx={{
      width: '100%',
      maxWidth: '100%'
    }}>
      <Button
        sx={{
          backgroundColor: `#1C1C1C !important`,
          height: 40,
          minWidth: 125,
          width: '100%',
          maxWidth: '100% !important',
          [theme.breakpoints.down('sm')]: {
            width: '100%',
          },
          justifyContent: 'space-between !important',
          textTransform: 'capitalize !important',
          padding: '4px 12px',
          border: `1px solid #101010`,
          color: theme.palette.common.white,
          fontWeight: 600,
          fontSize: 13,
          '&.MuiButton-outlinedPrimary': {
            color: theme.palette.common.white,
          },
          '& .MuiSvgIcon-root': {
            color: theme.palette.common.white,
          },
          '&.selected': {
            backgroundColor: theme.palette.grey[700],
            color: theme.palette.getContrastText(theme.palette.grey[700]),
            fontWeight: 500,
          },
        }}
        aria-controls="button-menu"
        aria-haspopup="true"
        variant="contained"
        color="primary"
        endIcon={<EndIcon />}
        disabled={locked}
        className={clsx(filters?.length > 0 && !singleSelect ? 'selected' : 'none')}
        {...bindTrigger(popupState)}
      >
        {getLabel(filters, defaultLabel)}
      </Button>

      <Menu
        sx={{
          width: "248px",
          '& .MuiList-root': {
            display: 'flex',
            flexDirection: 'column !important',
            justifyContent: 'space-between !important',
            alignItems: 'space-between !important',
            padding: '4px',
          },
          '& .MuiPaper-root': {
            maxWidth: '100%',
            width: "248px",
            border: '1px solid #d3d4d5',
          },
          '& .row': {
            display: 'flex',
            justifyContent: 'space-between',
            flexDirection: 'row',
            alignItems: 'center',
            width: '100%',

          },
        }}
        {...bindMenu({ ...popupState })}
      >
        {options
          ?.map((option: string) =>
            CustomMenuItem(option)
          )}
      </Menu>
    </Box>
  );
};

export default Component;
